import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import { cn } from "@/shared/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, onWheel, ...props }, ref) => {
	const isAvizoList = className?.includes("avizo-tabs-list");

	const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
		onWheel?.(e);
		if (!e.defaultPrevented && isAvizoList && e.deltaY !== 0 && !e.shiftKey) {
			let el: HTMLElement | null = e.currentTarget;
			let scrollContainer: HTMLElement | null = null;
			let depth = 0;
			while (el && el !== document.body && depth < 4) {
				if (el.scrollWidth > el.clientWidth) {
					scrollContainer = el;
					break;
				}
				el = el.parentElement;
				depth++;
			}

			if (scrollContainer) {
				scrollContainer.scrollLeft += e.deltaY;
			}
		}
	};

	return (
		<TabsPrimitive.List
			ref={ref}
			onWheel={handleWheel}
			className={cn(
				!isAvizoList &&
					"inline-flex h-11 sm:h-12 items-center justify-center rounded-xl bg-muted p-1.5 text-muted-foreground",
				className,
			)}
			{...props}
		/>
	);
});
TabsList.displayName = TabsPrimitive.List.displayName;

function parseTriggerChildren(children: React.ReactNode) {
	if (!children) return { content: children, isFormatted: true };

	const childArray = React.Children.toArray(children);
	if (childArray.length === 0) return { content: children, isFormatted: true };

	const hasExistingSquare = childArray.some(
		(child) =>
			React.isValidElement(child) &&
			(child.props as { className?: string })?.className?.includes?.("avizo-tab-square"),
	);
	if (hasExistingSquare) {
		return { content: children, isFormatted: true };
	}

	let iconNode: React.ReactNode = null;
	const labelNodes: React.ReactNode[] = [];

	for (const child of childArray) {
		if (
			!iconNode &&
			React.isValidElement(child) &&
			!(
				typeof child.props === "object" &&
				(child.props as { className?: string })?.className?.includes?.("avizo-tab-label")
			)
		) {
			iconNode = child;
		} else {
			labelNodes.push(child);
		}
	}

	if (iconNode) {
		const labelContent =
			labelNodes.length === 1 &&
			React.isValidElement(labelNodes[0]) &&
			(labelNodes[0].props as { className?: string })?.className?.includes?.("avizo-tab-label") ? (
				labelNodes[0]
			) : (
				<span className="avizo-tab-label">{labelNodes}</span>
			);

		return {
			content: (
				<>
					<span className="avizo-tab-square" aria-hidden="true">
						{iconNode}
					</span>
					{labelContent}
				</>
			),
			isFormatted: false,
		};
	}

	// Fallback if no icon element:
	const textVal = childArray
		.map((c) => (typeof c === "string" || typeof c === "number" ? String(c) : ""))
		.join("")
		.trim();
	const initial = textVal.charAt(0) || "•";

	const labelContent =
		childArray.length === 1 &&
		React.isValidElement(childArray[0]) &&
		(childArray[0].props as { className?: string })?.className?.includes?.("avizo-tab-label") ? (
			childArray[0]
		) : (
			<span className="avizo-tab-label">{children}</span>
		);

	return {
		content: (
			<>
				<span className="avizo-tab-square avizo-tab-square-fallback" aria-hidden="true">
					<span className="text-xs font-bold uppercase select-none opacity-80">
						{initial}
					</span>
				</span>
				{labelContent}
			</>
		),
		isFormatted: false,
	};
}

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, children, onClick, ...props }, ref) => {
	const isAvizoTrigger = className?.includes("avizo-tabs-trigger");

	let renderedChildren = children;
	if (isAvizoTrigger && children) {
		const { content, isFormatted } = parseTriggerChildren(children);
		if (!isFormatted) {
			renderedChildren = content;
		}
	}

	return (
		<TabsPrimitive.Trigger
			ref={ref}
			onClick={(e) => {
				onClick?.(e);
				if (
					isAvizoTrigger &&
					typeof (e.currentTarget as HTMLElement).scrollIntoView === "function"
				) {
					(e.currentTarget as HTMLElement).scrollIntoView({
						behavior: "smooth",
						block: "nearest",
						inline: "nearest",
					});
				}
			}}
			className={cn(
				!isAvizoTrigger &&
					"inline-flex items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm sm:text-base font-semibold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm",
				className,
			)}
			{...props}
		>
			{renderedChildren}
		</TabsPrimitive.Trigger>
	);
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			"mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
			className,
		)}
		{...props}
	/>
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export interface TabsDockProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	bleed?: boolean;
}

const TabsDock = React.forwardRef<HTMLDivElement, TabsDockProps>(
	({ className, children, bleed = true, ...props }, ref) => {
		const containerRef = React.useRef<HTMLDivElement | null>(null);
		const [canScrollLeft, setCanScrollLeft] = React.useState(false);
		const [canScrollRight, setCanScrollRight] = React.useState(false);

		const checkScroll = React.useCallback(() => {
			const el = containerRef.current;
			if (!el) return;
			const { scrollLeft, scrollWidth, clientWidth } = el;
			setCanScrollLeft(scrollLeft > 2);
			setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
		}, []);

		React.useEffect(() => {
			const el = containerRef.current;
			if (!el) return;
			checkScroll();

			const raf = requestAnimationFrame(checkScroll);
			el.addEventListener("scroll", checkScroll, { passive: true });
			window.addEventListener("resize", checkScroll, { passive: true });

			let ro: ResizeObserver | null = null;
			if (typeof ResizeObserver !== "undefined") {
				ro = new ResizeObserver(checkScroll);
				ro.observe(el);
				if (el.firstElementChild) ro.observe(el.firstElementChild);
			}

			return () => {
				cancelAnimationFrame(raf);
				el.removeEventListener("scroll", checkScroll);
				window.removeEventListener("resize", checkScroll);
				ro?.disconnect();
			};
		}, [checkScroll]);

		const scrollByAmount = (direction: "left" | "right") => {
			const el = containerRef.current;
			if (!el) return;
			const amount = direction === "left" ? -240 : 240;
			if (typeof el.scrollBy === "function") {
				el.scrollBy({ left: amount, behavior: "smooth" });
			} else {
				el.scrollLeft += amount;
			}
		};

		const maskStyle = React.useMemo(() => {
			const edgeAlpha = "rgba(0, 0, 0, 0.65)";
			const fadeLength = "12px";

			if (canScrollLeft && canScrollRight) {
				const mask = `linear-gradient(to right, ${edgeAlpha} 0, black ${fadeLength}, black calc(100% - ${fadeLength}), ${edgeAlpha} 100%)`;
				return { WebkitMaskImage: mask, maskImage: mask };
			}
			if (canScrollLeft) {
				const mask = `linear-gradient(to right, ${edgeAlpha} 0, black ${fadeLength}, black 100%)`;
				return { WebkitMaskImage: mask, maskImage: mask };
			}
			if (canScrollRight) {
				const mask = `linear-gradient(to right, black calc(100% - ${fadeLength}), ${edgeAlpha} 100%)`;
				return { WebkitMaskImage: mask, maskImage: mask };
			}
			return undefined;
		}, [canScrollLeft, canScrollRight]);

		return (
			<div
				ref={ref}
				className={cn(
					"relative group",
					bleed
						? "-mx-4 sm:-mx-6 lg:mx-0 w-[calc(100%+2rem)] sm:w-[calc(100%+3rem)] lg:w-full"
						: "w-full",
					className,
				)}
				{...props}
			>
				{canScrollLeft && (
					<button
						type="button"
						onClick={() => scrollByAmount("left")}
						aria-label="Rolar abas para a esquerda"
						className={cn(
							"hidden lg:flex absolute top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full items-center justify-center bg-background/80 hover:bg-background border border-border/60 shadow-md backdrop-blur text-foreground transition-all duration-150 active:scale-95 cursor-pointer",
							bleed ? "left-4 lg:left-0" : "left-0",
						)}
					>
						<ChevronLeft className="w-4 h-4" />
					</button>
				)}

				<div
					ref={containerRef}
					style={maskStyle}
					className={cn(
						"avizo-tabs-dock-wrapper",
						bleed ? "px-4 sm:px-6 lg:px-0" : "px-0",
					)}
				>
					{children}
				</div>

				{canScrollRight && (
					<button
						type="button"
						onClick={() => scrollByAmount("right")}
						aria-label="Rolar abas para a direita"
						className={cn(
							"hidden lg:flex absolute top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full items-center justify-center bg-background/80 hover:bg-background border border-border/60 shadow-md backdrop-blur text-foreground transition-all duration-150 active:scale-95 cursor-pointer",
							bleed ? "right-4 lg:right-0" : "right-0",
						)}
					>
						<ChevronRight className="w-4 h-4" />
					</button>
				)}
			</div>
		);
	},
);
TabsDock.displayName = "TabsDock";

export {
	Tabs,
	TabsContent,
	TabsDock,
	TabsList,
	TabsTrigger,
};

